import React, { FC, useEffect, useState } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Spin,
  Statistic,
  Row,
  Col,
  Badge,
  Timeline,
  Dropdown,
  message,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FlagOutlined,
  TagsOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { tasksApis } from "../../../api/NewTasks";
import { getColorByLabel } from "../projects/ProjectInformationById";
import { useWbsName } from "../shared/useWbsCodes";

interface SubTaskQuickViewCardProps {
  subTaskId?: number | null;
  derivedProjectStatus?: string;
  onClose?: () => void;
  onEdit?: () => void;
  onRefresh?: () => void;
  showEditButton?: boolean;
}

const getStatusColor = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "inprogress" || s === "in progress")
    return "processing";
  if (s === "completed" || s === "closed" || s === "approved") return "success";
  if (s === "onhold" || s === "on hold") return "warning";
  if (s === "cancelled" || s === "deleted" || s === "rejected") return "error";
  if (s === "draft" || s === "yettostart" || s === "yet to start")
    return "default";
  return "default";
};

const getPriorityColor = (priority: string) => {
  const p = (priority || "").toLowerCase();
  if (p === "high" || p === "critical" || p === "urgent") return "red";
  if (p === "medium") return "orange";
  if (p === "low") return "blue";
  return "default";
};

const formatDate = (d: any) =>
  d ? dayjs(d).format("DD MMM YYYY") : "Not Set";

const SubTaskQuickViewCard: FC<SubTaskQuickViewCardProps> = ({
  subTaskId,
  derivedProjectStatus,
  onClose,
  onEdit,
  onRefresh,
  showEditButton = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [subtask, setSubtask] = useState<any>(null);
  const wbsName = useWbsName();
  const [statusList, setStatusList] = useState<
    { label: string; key: string; value: string }[]
  >([]);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [messageApi, msgContext] = message.useMessage();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  useEffect(() => {
    if (subTaskId) fetchSubtask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTaskId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await tasksApis.fetchTaskStatusList(
          "TaskStatusChange"
        );
        const { data, status } = response || {};
        if (status && mounted) {
          const desiredOrder = [
            "Open",
            "OnHold",
            "InProgress",
            "UnderQC",
            "Closed",
          ];
          const formatted = desiredOrder
            .map((s) => {
              const match = (data || []).find(
                (d: string) => d.toLowerCase() === s.toLowerCase()
              );
              return match ? { label: match, key: match, value: match } : null;
            })
            .filter(Boolean) as { label: string; key: string; value: string }[];
          setStatusList(formatted);
        }
      } catch {
        // silently ignore — dropdown just won't show
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const performStatusChange = async (newStatus: string) => {
    if (!subtask?.subTaskId) return;
    setStatusUpdating(true);
    try {
      const response = await tasksApis.SubTaskstatusList(
        subtask.subTaskId,
        newStatus
      );
      const { status, message: msg } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: msg || `Subtask status updated to ${newStatus} successfully!`,
          confirmButtonText: "Ok",
        });
        await fetchSubtask();
        onRefresh?.();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: msg || "Failed to update status.",
          confirmButtonText: "Ok",
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update status. Please try again.",
        confirmButtonText: "Ok",
      });
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleStatusMenuClick = async ({ key }: { key: string }) => {
    const newStatus = key;
    const currentStatus = subtask?.subTaskStatus || subtask?.status;
    if (!newStatus || newStatus === currentStatus) return;

    // OnHold transitions need reason + ETA — defer to the edit view.
    if (
      newStatus.toLowerCase() === "onhold" &&
      ["inprogress", "underqc", "active", "open"].includes(
        (currentStatus || "").toLowerCase()
      )
    ) {
      messageApi.info("Switching to edit view to capture hold reason.");
      onEdit?.();
      return;
    }

    await performStatusChange(newStatus);
  };

  const fetchSubtask = async () => {
    if (!subTaskId) return;
    setLoading(true);
    try {
      const response: any = await tasksApis.fetchSubtaskDetailsById(
        Number(subTaskId),
        derivedProjectStatus
      );
      if (response?.status && response?.data) {
        setSubtask(response.data);
      } else {
        setSubtask(null);
      }
    } catch (err) {
      console.error("Failed to fetch subtask:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!subtask) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
        No subtask data available
      </div>
    );
  }

  const status = subtask.subTaskStatus || subtask.status;
  const priority = subtask.priorityName || subtask.subTaskPriorityName;
  const flag = subtask.subTaskFlag || subtask.flag;
  const tags: any[] = subtask.subTaskTags || subtask.taskTags || [];

  // Status-change gating mirrors TaskQuickViewCard.
  const projectStatus = (
    subtask.projectStatus ||
    derivedProjectStatus ||
    ""
  ).toLowerCase();
  const phaseStatus = (subtask.phaseStatus || "").toLowerCase();
  const activityStatus = (subtask.activityStatus || "").toLowerCase();
  const parentTaskStatus = (subtask.taskStatus || "").toLowerCase();
  const blocked = ["onhold", "cancelled", "archived", "closed", "deleted"];
  const baseConditionsMet =
    !blocked.includes(projectStatus) &&
    !blocked.includes(phaseStatus) &&
    !blocked.includes(activityStatus) &&
    !blocked.includes(parentTaskStatus);
  const isInHierarchy =
    Array.isArray(subtask.hierarchyUserIds) &&
    !!currentUser?.id &&
    subtask.hierarchyUserIds
      .filter((id: number) => id > 0)
      .includes(currentUser.id);
  const isAdmin =
    (currentUser?.genericRole || "").toLowerCase() === "admin";
  const subtaskOwnerId = subtask.subTaskOwnerId || subtask.taskOwnerId;
  const isOwner = subtaskOwnerId === currentUser?.id;
  const statusChangeAllowed = !["closed", "archived"].includes(
    (status || "").toLowerCase()
  );
  const allowUpdates =
    subtask?.allowSubtaskUpdates !== false &&
    subtask?.allowSubTaskUpdates !== false &&
    subtask?.allowTaskUpdates !== false;
  const canChangeStatus =
    allowUpdates &&
    baseConditionsMet &&
    statusChangeAllowed &&
    (isOwner || isAdmin || isInHierarchy);

  const plannedStart =
    subtask.subTaskPlannedStartDate || subtask.taskPlannedStartDate;
  const plannedEnd =
    subtask.subTaskPlannedEndDate || subtask.taskPlannedEndDate;
  const actualStart = subtask.subTaskActualStartDate;
  const actualEnd = subtask.subTaskActualEndDate;

  const plannedDurationDays =
    plannedStart && plannedEnd
      ? Math.max(0, dayjs(plannedEnd).diff(dayjs(plannedStart), "day") + 1)
      : null;

  // Quick stat reflects the subtask state:
  //  - completed (has actualEnd):  actual duration = actualEnd - actualStart + 1
  //  - open + today > plannedEnd:  Overdue By X days
  //  - open + today <= plannedEnd: Due In X days
  const isCompleted = !!actualEnd;
  const completedDuration =
    actualEnd && actualStart
      ? Math.max(
          0,
          dayjs(actualEnd).startOf("day").diff(dayjs(actualStart).startOf("day"), "day") + 1
        )
      : null;
  const daysUntilDue = !actualEnd && plannedEnd
    ? dayjs(plannedEnd).startOf("day").diff(dayjs().startOf("day"), "day")
    : null;

  const isOverdue = !isCompleted && daysUntilDue !== null && daysUntilDue < 0;

  const dueCard = (() => {
    if (isCompleted) {
      if (completedDuration === null) {
        return {
          title: "Completed",
          value: "-" as number | string,
          suffix: null as string | null,
          color: "#389e0d",
          bg: "#f6ffed",
          border: "1px solid #b7eb8f",
        };
      }
      return {
        title: "Completed In",
        value: completedDuration,
        suffix: completedDuration === 1 ? "day" : "days",
        color: "#389e0d",
        bg: "#f6ffed",
        border: "1px solid #b7eb8f",
      };
    }
    if (daysUntilDue === null) {
      return {
        title: "Days Until Due",
        value: "-" as number | string,
        suffix: null,
        color: "#0958d9",
        bg: "#f0f9ff",
        border: "1px solid #bae0ff",
      };
    }
    const abs = Math.abs(daysUntilDue);
    const label = abs === 1 ? "day" : "days";
    return isOverdue
      ? {
          title: "Overdue By",
          value: abs,
          suffix: label,
          color: "#cf1322",
          bg: "#fff1f0",
          border: "1px solid #ffa39e",
        }
      : {
          title: "Due In",
          value: abs,
          suffix: label,
          color: "#0958d9",
          bg: "#f0f9ff",
          border: "1px solid #bae0ff",
        };
  })();

  const estimatedHours = subtask.workHoursInSec
    ? Math.round((Number(subtask.workHoursInSec) || 0) / 3600)
    : null;

  return (
    <div style={{ padding: "20px 24px" }}>
      {msgContext}
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h4
          style={{
            margin: "0 0 8px 0",
            fontSize: 20,
            fontWeight: 600,
            color: "#1a1a2e",
            wordBreak: "break-word",
          }}
        >
          {subtask.subTaskName || subtask.taskName || "Subtask"}
        </h4>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {status ? (
            canChangeStatus && statusList.length > 0 ? (
              <Dropdown
                menu={{
                  items: statusList,
                  selectedKeys: [status],
                  onClick: handleStatusMenuClick,
                }}
                trigger={["click"]}
                placement="bottomLeft"
                disabled={statusUpdating}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "2px 10px",
                    borderRadius: 12,
                    background: "#f5f5f5",
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: "22px",
                    cursor: statusUpdating ? "wait" : "pointer",
                    border: "1px dashed #d9d9d9",
                  }}
                  title="Click to change status"
                >
                  <Badge status={getStatusColor(status)} />
                  {status}
                  <DownOutlined style={{ fontSize: 9, color: "#666" }} />
                </span>
              </Dropdown>
            ) : (
              <Badge
                status={getStatusColor(status)}
                text={
                  <span style={{ fontWeight: 500, fontSize: 14 }}>{status}</span>
                }
              />
            )
          ) : null}
          {priority ? (
            <Tag color={getPriorityColor(priority)}>
              <FlagOutlined /> {priority}
            </Tag>
          ) : null}
          {flag ? (
            <Tag color={flag === "Internal" ? "blue" : "orange"}>
              <FlagOutlined /> {flag}
            </Tag>
          ) : null}
        </div>
      </div>

      {/* Progress / Timeline Card (gradient) */}
      <Card
        size="small"
        style={{
          marginBottom: 20,
          borderRadius: 12,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
        }}
        styles={{ body: { padding: "16px 20px" } }}
      >
        <Row gutter={16} align="middle">
          <Col span={24}>
            <Statistic
              title={
                <span
                  style={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: 12,
                  }}
                >
                  Planned Duration
                </span>
              }
              value={plannedDurationDays ?? 0}
              suffix={
                <span
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 14,
                  }}
                >
                  days
                </span>
              }
              valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 600 }}
            />
            <div
              style={{
                color: "rgba(255,255,255,0.85)",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              {plannedStart && plannedEnd
                ? `${dayjs(plannedStart).format("DD MMM")} → ${dayjs(
                    plannedEnd
                  ).format("DD MMM YYYY")}`
                : "Dates not set"}
            </div>
          </Col>
        </Row>
      </Card>

      {/* Quick Stats */}
      <Row gutter={12} style={{ marginBottom: 20 }}>
        <Col span={estimatedHours ? 12 : 24}>
          <Card
            size="small"
            style={{
              borderRadius: 10,
              background: dueCard.bg,
              border: dueCard.border,
            }}
            styles={{ body: { padding: "12px 16px" } }}
          >
            <Statistic
              title={
                <span style={{ fontSize: 12, color: dueCard.color }}>
                  {dueCard.title}
                </span>
              }
              value={dueCard.value}
              suffix={
                dueCard.suffix ? (
                  <span style={{ fontSize: 12, color: dueCard.color }}>
                    {dueCard.suffix}
                  </span>
                ) : null
              }
              valueStyle={{ color: dueCard.color, fontSize: 22 }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        {estimatedHours ? (
          <Col span={12}>
            <Card
              size="small"
              style={{
                borderRadius: 10,
                background: "#f6ffed",
                border: "1px solid #b7eb8f",
              }}
              styles={{ body: { padding: "12px 16px" } }}
            >
              <Statistic
                title={
                  <span style={{ fontSize: 12, color: "#389e0d" }}>
                    Estimated Hours
                  </span>
                }
                value={estimatedHours}
                suffix={
                  <span style={{ fontSize: 12, color: "#389e0d" }}>hrs</span>
                }
                valueStyle={{ color: "#389e0d", fontSize: 22 }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
        ) : null}
      </Row>

      {/* Subtask Details */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            Subtask Details
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
        styles={{
          header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
          body: { padding: "12px 16px" },
        }}
      >
        <Descriptions
          column={{ xs: 1, sm: 1, md: 2 }}
          size="small"
          labelStyle={{
            fontWeight: 500,
            color: "#666",
            width: 160,
            verticalAlign: "top",
          }}
          contentStyle={{ wordBreak: "break-word" }}
        >
          <Descriptions.Item
            label={
              <>
                <UserOutlined style={{ marginRight: 6 }} />
                Owner
              </>
            }
          >
            {subtask.subTaskOwnerName || subtask.taskOwnerName || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Planned Start
              </>
            }
          >
            {formatDate(plannedStart)}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Planned End
              </>
            }
          >
            {formatDate(plannedEnd)}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Actual Start
              </>
            }
          >
            {actualStart ? dayjs(actualStart).format("DD MMM YYYY") : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Actual End
              </>
            }
          >
            {actualEnd ? dayjs(actualEnd).format("DD MMM YYYY") : "N/A"}
          </Descriptions.Item>
          {subtask.contractorName ? (
            <Descriptions.Item
              label={
                <>
                  <UserOutlined style={{ marginRight: 6 }} />
                  Contractor
                </>
              }
            >
              {subtask.contractorName}
            </Descriptions.Item>
          ) : null}
          {subtask.isBillable !== undefined ? (
            <Descriptions.Item
              label={
                <>
                  <DollarOutlined style={{ marginRight: 6 }} />
                  Billable
                </>
              }
            >
              <Tag color={subtask.isBillable ? "green" : "default"}>
                {subtask.isBillable ? "Yes" : "No"}
              </Tag>
            </Descriptions.Item>
          ) : null}
          {subtask.approvalRequired !== undefined ? (
            <Descriptions.Item
              label={
                <>
                  <CheckCircleOutlined style={{ marginRight: 6 }} />
                  Approval Required
                </>
              }
            >
              <Tag color={subtask.approvalRequired ? "blue" : "default"}>
                {subtask.approvalRequired ? "Yes" : "No"}
              </Tag>
            </Descriptions.Item>
          ) : null}
          {(() => {
            const createdDate =
              subtask.createdAt ||
              subtask.subTaskCreatedAt ||
              subtask.createdOn ||
              subtask.createdDate;
            const createdBy =
              subtask.createdUserName || subtask.createdByUserName;
            if (!createdDate && !createdBy) return null;
            return (
              <Descriptions.Item
                label={
                  <>
                    <ClockCircleOutlined style={{ marginRight: 6 }} />
                    Created
                  </>
                }
              >
                {createdDate ? (
                  <>
                    {dayjs(createdDate).format("DD MMM YYYY")}
                    {createdBy ? (
                      <span style={{ color: "#999", marginLeft: 8 }}>
                        by {createdBy}
                      </span>
                    ) : null}
                  </>
                ) : (
                  createdBy
                )}
              </Descriptions.Item>
            );
          })()}
          {(() => {
            const updatedDate =
              subtask.updatedAt ||
              subtask.subTaskUpdatedAt ||
              subtask.updatedOn;
            const createdDate =
              subtask.createdAt ||
              subtask.subTaskCreatedAt ||
              subtask.createdOn ||
              subtask.createdDate;
            const updatedBy =
              subtask.updatedUserName || subtask.updatedByUserName;
            if (!updatedDate || updatedDate === createdDate) return null;
            return (
              <Descriptions.Item
                label={
                  <>
                    <ClockCircleOutlined style={{ marginRight: 6 }} />
                    Updated
                  </>
                }
              >
                {dayjs(updatedDate).format("DD MMM YYYY")}
                {updatedBy ? (
                  <span style={{ color: "#999", marginLeft: 8 }}>
                    by {updatedBy}
                  </span>
                ) : null}
              </Descriptions.Item>
            );
          })()}
          {subtask.autoApprovalRemarks ? (
            <Descriptions.Item
              label={
                <>
                  <FileTextOutlined style={{ marginRight: 6 }} />
                  Auto Approval Remarks
                </>
              }
              span={2}
            >
              {subtask.autoApprovalRemarks}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>

      {/* Description */}
      {(subtask.subTaskDescription || subtask.taskDescription) && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <FileTextOutlined style={{ marginRight: 6 }} />
              Description
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{
            header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
            body: { padding: "12px 16px" },
          }}
        >
          <div
            style={{
              whiteSpace: "pre-wrap",
              color: "#262626",
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            {subtask.subTaskDescription || subtask.taskDescription}
          </div>
        </Card>
      )}

      {/* Assignees */}
      {Array.isArray(subtask.subTaskUsers) &&
        subtask.subTaskUsers.filter(
          (u: any) => u?.subTaskUserType !== "Owner"
        ).length > 0 && (
          <Card
            size="small"
            title={
              <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
                <UserOutlined style={{ marginRight: 6 }} />
                Assignees (
                {
                  subtask.subTaskUsers.filter(
                    (u: any) => u?.subTaskUserType !== "Owner"
                  ).length
                }
                )
              </span>
            }
            style={{ borderRadius: 12, marginBottom: 20 }}
            styles={{
              header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
              body: { padding: "12px 16px" },
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {subtask.subTaskUsers
                .filter((u: any) => u?.subTaskUserType !== "Owner")
                .map((u: any, idx: number) => (
                  <Tag
                    key={u?.projectUserId ?? idx}
                    icon={<UserOutlined />}
                    color="blue"
                  >
                    {u?.userName || u?.fullName || `User ${u?.projectUserId}`}
                  </Tag>
                ))}
            </div>
          </Card>
        )}

      {/* Reminders */}
      {(subtask.reminderFlag || subtask.reminders) && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <ClockCircleOutlined style={{ marginRight: 6 }} />
              Reminders
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{
            header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
            body: { padding: "12px 16px" },
          }}
        >
          <Descriptions
            column={{ xs: 1, sm: 2 }}
            size="small"
            labelStyle={{ fontWeight: 500, color: "#666", width: 140 }}
            contentStyle={{ wordBreak: "break-word" }}
          >
            {subtask.reminders?.frequency ? (
              <Descriptions.Item label="Frequency">
                {subtask.reminders.frequency}
              </Descriptions.Item>
            ) : null}
            {subtask.reminders?.daysOffset != null ? (
              <Descriptions.Item label="Days Offset">
                {subtask.reminders.daysOffset}
              </Descriptions.Item>
            ) : null}
            {subtask.reminders?.specificTime ? (
              <Descriptions.Item label="Specific Time">
                {subtask.reminders.specificTime}
              </Descriptions.Item>
            ) : null}
            {subtask.reminders?.notifyVia ? (
              <Descriptions.Item label="Notify Via">
                {Array.isArray(subtask.reminders.notifyVia)
                  ? subtask.reminders.notifyVia.join(", ")
                  : subtask.reminders.notifyVia}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </Card>
      )}

      {/* Hierarchy */}
      {(subtask.projectTitle ||
        subtask.phaseName ||
        subtask.activityName ||
        subtask.taskName ||
        subtask.parentTaskName) && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              Hierarchy
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{
            header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
            body: { padding: "12px 16px" },
          }}
        >
          <Descriptions
            column={1}
            size="small"
            labelStyle={{ fontWeight: 500, color: "#666", width: 140 }}
          >
            {subtask.projectTitle ? (
              <Descriptions.Item label="Project">
                {subtask.projectTitle}
              </Descriptions.Item>
            ) : null}
            {subtask.phaseName ? (
              <Descriptions.Item label="Phase">
                {wbsName(subtask.phaseName)}
              </Descriptions.Item>
            ) : null}
            {subtask.activityName ? (
              <Descriptions.Item label="Activity">
                {wbsName(subtask.activityName)}
              </Descriptions.Item>
            ) : null}
            {subtask.parentTaskName || subtask.taskName ? (
              <Descriptions.Item label="Parent Task">
                {subtask.parentTaskName || subtask.taskName}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </Card>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <TagsOutlined style={{ marginRight: 8 }} />
              Tags
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{
            header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
            body: { padding: "12px 16px" },
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {tags.map((tag: any, index: number) => {
              const color = getColorByLabel(tag?.tagName || "");
              return (
                <Tag
                  key={tag?.tagId ?? index}
                  style={{
                    backgroundColor: color,
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 10px",
                  }}
                >
                  {tag?.tagName || "-"}
                </Tag>
              );
            })}
          </div>
        </Card>
      )}

      {/* On Hold Info */}
      {(status || "").toLowerCase() === "onhold" && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#d48806" }}>
              On Hold Information
            </span>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            background: "#fffbe6",
            border: "1px solid #ffe58f",
          }}
          styles={{
            header: {
              borderBottom: "1px solid #ffe58f",
              minHeight: 40,
              background: "#fffbe6",
            },
            body: { padding: "12px 16px" },
          }}
        >
          <Timeline
            items={[
              {
                color: "orange",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Hold Date</div>
                    <div style={{ color: "#666" }}>
                      {subtask.subTaskOnholdDate || subtask.onHoldDate
                        ? dayjs(
                            subtask.subTaskOnholdDate || subtask.onHoldDate
                          ).format("DD MMM YYYY")
                        : "N/A"}
                    </div>
                  </>
                ),
              },
              {
                color: "green",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Expected Resolution</div>
                    <div style={{ color: "#666" }}>
                      {subtask.subTaskETAResolution || subtask.etaResolution
                        ? dayjs(
                            subtask.subTaskETAResolution ||
                              subtask.etaResolution
                          ).format("DD MMM YYYY")
                        : "N/A"}
                    </div>
                  </>
                ),
              },
              {
                color: "blue",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Reason</div>
                    <div style={{ color: "#666" }}>
                      {subtask.subTaskOnholdReason ||
                        subtask.onHoldReason ||
                        "No reason provided"}
                    </div>
                  </>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

export default SubTaskQuickViewCard;
